import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from "react";

export function AdsHelpHint({ label, children }: { label: string; children: ReactNode }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const el = rootRef.current;
      if (!el || !(e.target instanceof Node) || el.contains(e.target)) return;
      close();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDocMouseDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [open, close]);

  return (
    <span ref={rootRef} className="relative inline-flex max-w-full align-middle">
      <button
        type="button"
        id={`${id}-trigger`}
        aria-expanded={open}
        aria-controls={`${id}-panel`}
        className="inline-flex h-5 min-w-[1.25rem] cursor-pointer select-none items-center justify-center rounded-full border border-blue-300/90 bg-white/80 px-1 text-[10px] font-bold leading-none text-blue-900 shadow-sm hover:bg-blue-50 dark:border-blue-700 dark:bg-blue-950/50 dark:text-blue-100 dark:hover:bg-blue-900/60"
        aria-label={`${label} — more detail`}
        title={label}
        onClick={() => setOpen((v) => !v)}
      >
        ?
      </button>
      {open ? (
        <div
          id={`${id}-panel`}
          role="dialog"
          aria-labelledby={`${id}-trigger`}
          tabIndex={-1}
          className="absolute left-0 top-[calc(100%+4px)] z-50 max-w-[min(100vw-1.5rem,24rem)] min-w-[12rem] rounded-lg border border-blue-100 bg-white/95 px-2.5 py-2 text-xs font-normal font-sans leading-relaxed text-blue-950 shadow-lg dark:border-blue-900 dark:bg-blue-950/95 dark:text-blue-50"
        >
          {children}
        </div>
      ) : null}
    </span>
  );
}

type CollapsibleProps = {
  sectionId: string;
  title: string;
  subtitle?: ReactNode;
  /** e.g. clustered ? help for this block */
  titleActions?: ReactNode;
  /** Initial open state; native details element so the browser handles open/close. */
  defaultOpen?: boolean;
  variant?: "default" | "indigo";
  /** Extra classes on the body under the summary (e.g. match variant border tint). */
  bodyClassName?: string;
  children: ReactNode;
};

/**
 * Native &lt;details&gt; section — keeps large Ads areas scannable; pair with {@link AdsHelpHint} for inline docs.
 */
export function AdsCollapsibleSection({
  sectionId,
  title,
  subtitle,
  titleActions,
  defaultOpen = false,
  variant = "default",
  bodyClassName,
  children,
}: CollapsibleProps) {
  const shell =
    variant === "indigo"
      ? "rounded-2xl border border-indigo-200/80 bg-indigo-50/30 shadow-sm dark:border-indigo-900/60 dark:bg-indigo-950/20"
      : "rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-zinc-900";

  return (
    <details
      id={sectionId}
      className={`group ${shell}`}
      // React 19 supports defaultOpen on <details>; DOM lib types may lag.
      {...(defaultOpen ? { defaultOpen: true } : {})}
      aria-label={title}
    >
      <summary className="flex cursor-pointer list-none flex-wrap items-start gap-2 px-4 py-3 select-none [&::-webkit-details-marker]:hidden">
        <span className="mt-0.5 inline-block shrink-0 text-gray-400 transition-transform group-open:rotate-90 dark:text-gray-500">
          ▸
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
          {subtitle ? (
            <div className="mt-1 text-xs text-gray-600 dark:text-gray-400 [&_p]:max-w-prose">{subtitle}</div>
          ) : null}
        </div>
        {titleActions ? (
          <div className="flex max-w-full flex-wrap items-center justify-end gap-1.5 sm:max-w-[min(100%,28rem)]">
            {titleActions}
          </div>
        ) : null}
      </summary>
      <div
        className={`border-t border-gray-200/90 px-4 pb-4 pt-3 dark:border-gray-700/90${bodyClassName ? ` ${bodyClassName}` : ""}`}
      >
        {children}
      </div>
    </details>
  );
}
